import { useEffect, useRef } from "react";

import { sharedMemoryCache } from "src-next/shared/cache/createMemoryCache";
import { useCachedResource } from "src-next/shared/cache/useCachedResource";

import type {
  FluxZapBatchTaskView,
  FluxZapCommunicationRecord,
} from "../types";
import {
  CLINICAL_CRM_COMMUNICATIONS_EMPTY_DATA,
  type ClinicalCrmCommunicationsData,
  clinicalCrmCommunicationsService,
} from "../services/clinicalCrmCommunicationsService";

export interface UseClinicalCrmCommunicationsDataOptions {
  /**
   * Sem isto a seção recolhida não busca. Depois da primeira abertura
   * o caller mantém `true` para o refresh de envio atualizar o histórico.
   */
  enabled?: boolean;
}

export interface UseClinicalCrmCommunicationsDataResult {
  communications: FluxZapCommunicationRecord[];
  batchTasks: FluxZapBatchTaskView[];
  isLoading: boolean;
  loadError: unknown;
  refetch: () => void;
}

/** Chaves marcadas por um envio enquanto ninguém estava ouvindo. */
const staleCommunicationKeys = new Set<string>();

function communicationsCacheKey(organizationId: string | undefined): string | null {
  const trimmed = organizationId?.trim() || "";
  if (!trimmed) return null;
  return `crm:communications:${trimmed}`;
}

/**
 * Carrega Basic (comunicações) e Task (disparos em lote) via FHIR para a seção de gestão.
 * A lista fica em memória até o botão atualizar ou um envio (`refreshSignal`).
 */
export function useClinicalCrmCommunicationsData(
  organizationId: string | undefined,
  refreshSignal: number = 0,
  options: UseClinicalCrmCommunicationsDataOptions = {},
): UseClinicalCrmCommunicationsDataResult {
  const enabled = options.enabled ?? true;
  const cacheKey = communicationsCacheKey(organizationId);
  const organizationRef = useRef(organizationId);
  organizationRef.current = organizationId;

  const { data, isLoading, error, refresh } =
    useCachedResource<ClinicalCrmCommunicationsData>({
      cacheKey,
      enabled: enabled && Boolean(cacheKey),
      fetcher: () => {
        const trimmed = organizationRef.current?.trim() || "";
        return clinicalCrmCommunicationsService.fetchClinicalCrmCommunicationsData(
          trimmed,
        );
      },
    });

  const signalRef = useRef(refreshSignal);
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;

  useEffect(() => {
    const signalChanged = signalRef.current !== refreshSignal;
    if (signalChanged) signalRef.current = refreshSignal;
    if (!cacheKey) return;

    if (!enabled) {
      // Seção ainda fechada: não busca agora. A próxima abertura vê o stale.
      if (signalChanged) staleCommunicationKeys.add(cacheKey);
      return;
    }

    const isStale = staleCommunicationKeys.has(cacheKey);
    if (!signalChanged && !isStale) return;
    staleCommunicationKeys.delete(cacheKey);

    const snapshot = sharedMemoryCache.getKeySnapshot(cacheKey);
    // Sem dado, o `useCachedResource` já busca. Com dado velho, força a rede.
    if (snapshot.entry) void refreshRef.current();
  }, [cacheKey, enabled, refreshSignal]);

  const refetch = () => {
    if (cacheKey) staleCommunicationKeys.delete(cacheKey);
    void refresh();
  };

  return {
    communications:
      data?.communications ??
      CLINICAL_CRM_COMMUNICATIONS_EMPTY_DATA.communications,
    batchTasks:
      data?.batchTasks ?? CLINICAL_CRM_COMMUNICATIONS_EMPTY_DATA.batchTasks,
    isLoading,
    loadError: error,
    refetch,
  };
}
